import React from 'react';
import './TableView.css';
import Icon from './Icon';

const CompanyLogo = ({ contact, size = 20, className = '' }) => {
  const [logoUrl, setLogoUrl] = React.useState(null);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    const companyDetail = contact.company_detail || contact.current_company_detail || contact.primary_company;
    const logoUuid = companyDetail?.logo_uuid || contact.logo_uuid;
    
    console.log('TableView CompanyLogo - Contact logo data:', {
      contactId: contact.id,
      companyName: contact.company || contact.company_name || companyDetail?.name,
      logoUuid: logoUuid,
      company_detail: contact.company_detail,
      current_company_detail: contact.current_company_detail,
      primary_company: contact.primary_company,
      contact_logo_uuid: contact.logo_uuid,
      company_logo_url: contact.company_logo_url
    });
    
    if (logoUuid) {
      const url = `${process.env.REACT_APP_API_BASE_URL || ''}/api/v2/logos/${logoUuid}`;
      setLogoUrl(url);
      console.log('Setting logo URL from UUID:', url);
    } else if (contact.company_logo_url) {
      setLogoUrl(contact.company_logo_url);
      console.log('Setting logo URL from company_logo_url:', contact.company_logo_url);
    } else {
      console.log('No logo data found for contact:', contact.id);
    }
  }, [contact]);

  const handleError = () => {
    setHasError(true);
  };

  const handleLoad = () => {
    setHasError(false);
  };

  if (!logoUrl || hasError) {
    return (
      <span 
        className={`company-logo-placeholder ${className}`}
        style={{ 
          width: size, 
          height: size, 
          fontSize: Math.max(13, size * 0.8),
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Icon name="building" />
      </span>
    );
  }

  return (
    <img
      src={logoUrl}
      alt="Company Logo"
      className={`company-logo-img ${className}`}
      style={{ 
        width: size, 
        height: size, 
        borderRadius: '50%',
        objectFit: 'cover',
        backgroundColor: 'var(--background-color-alpha)'
      }}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
};

const TableView = ({
  contacts,
  filterContact,
  setFilterContact,
  activeApiFilters,
  setActiveApiFilters,
  resetAllFilters,
  setSelectedNodeData,
  theme = 'light',
  onSortChange = () => {},
  activeFiltersSummary = []
}) => {
  const [sortField, setSortField] = React.useState(null);
  const [sortOrder, setSortOrder] = React.useState('asc');

  const filteredContacts = contacts || [];

  const handleSort = (field) => {
    let newOrder = 'asc';
    if (sortField === field && sortOrder === 'asc') {
      newOrder = 'desc';
    }
    
    setSortField(field);
    setSortOrder(newOrder);
    
    onSortChange(field, newOrder);
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return null;
    return <Icon name={sortOrder === 'asc' ? 'arrow-up' : 'arrow-down'} className="sort-indicator" />;
  };

  return (
    <div className="table-view-container">
      <div className="table-header">
        <span>Showing {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''}</span>
      </div>
      
      <table className="table-main">
        <thead>
          <tr className="table-header-row">
            <th className="table-header-cell table-header-cell--sortable" onClick={() => handleSort('name')}>
              Name{getSortIndicator('name')}
            </th>
            <th className="table-header-cell table-header-cell--sortable" onClick={() => handleSort('company')}>
              Company{getSortIndicator('company')}
            </th>
            <th className="table-header-cell table-header-cell--sortable" onClick={() => handleSort('position')}>
              Position{getSortIndicator('position')}
            </th>
            <th className="table-header-cell table-header-cell--sortable" onClick={() => handleSort('location')}>
              Location{getSortIndicator('location')}
            </th>
            <th className="table-header-cell table-header-cell--sortable" onClick={() => handleSort('industry')}>
              Industry{getSortIndicator('industry')}
            </th>
            <th className="table-header-cell table-header-cell--sortable" onClick={() => handleSort('advisor')}>
              Advisor{getSortIndicator('advisor')}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredContacts.map((contact, index) => (
            <tr
              key={contact.id}
              className="table-row"
              onClick={(e) => {
                if ((e.ctrlKey || e.metaKey) && (contact.linkedin_url || contact.url)) {
                  window.open(contact.linkedin_url || contact.url, '_blank', 'noopener,noreferrer');
                } else {
                  setSelectedNodeData({ group: 'contact', contactData: contact });
                }
              }}
              title={contact.linkedin_url || contact.url ? "Click to view details, Ctrl+Click to open LinkedIn" : "Click to view details"}
            >
              <td className="table-cell">
                {contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || `Contact ${contact.id}`}
              </td>
              <td className="table-cell">
                <div className="company-cell">
                  <CompanyLogo 
                    contact={contact}
                    size={20}
                    className="company-logo"
                  />
                  <span className="company-name">
                    {contact.company || 
                     contact.company_name || 
                     contact.company_detail?.name || 
                     contact.current_company_detail?.name || 
                     contact.primary_company?.name || 'N/A'}
                  </span>
                </div>
              </td>
              <td className="table-cell">
                {contact.title || 
                 contact.headline || 
                 contact.position_detail?.name || 
                 contact.position_name || 
                 contact.job_title || 'N/A'}
              </td>
              <td className="table-cell">
                {[contact.city_name, contact.country_name].filter(Boolean).join(', ') || 
                 [contact.city?.name || contact.city, contact.country?.name || contact.country].filter(Boolean).join(', ') || 'N/A'}
              </td>
              <td className="table-cell">
                {contact.industry || 
                 contact.sector || 
                 contact.company_detail?.industry || 
                 contact.current_company_detail?.industry || 
                 contact.primary_company?.industry || 'Unknown'}
              </td>
              <td className="table-cell">
                {contact.advisor_name || contact.advisor || 'N/A'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredContacts.length === 0 && (
        <div className="table-no-results">
          No contacts found matching the current filters.
        </div>
      )}
    </div>
  );
};

export default TableView;
